import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { api, extractApiError } from '../api/client';
import StarRating from '../components/StarRating';
import { SkeletonMetricCard, SkeletonChart } from '../components/SkeletonLoader';
import type { FeedbackAnalytics, Feedback } from '@shared/types';

const CATEGORY_COLORS: Record<string, string> = {
  ux: '#6366f1', feature: '#22c55e', bug: '#ef4444', pricing: '#f59e0b', other: '#a855f7',
};

const NPS_COLOR = (score: number) => score > 70 ? '#a855f7' : score > 30 ? '#22c55e' : score >= 0 ? '#f59e0b' : '#ef4444';

export default function FeedbackPage() {
  const { id: startupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<FeedbackAnalytics | null>(null);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'rating' | 'nps'>('newest');

  useEffect(() => {
    if (!startupId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [analyticsRes, feedbackRes] = await Promise.all([
          api.get(`/feedback/startup/${startupId}/analytics`),
          api.get(`/feedback/startup/${startupId}`),
        ]);
        setAnalytics(analyticsRes.data.data.analytics);
        setFeedback(feedbackRes.data.data.feedback);
      } catch (err) {
        console.error(extractApiError(err).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [startupId]);

  const filteredFeedback = feedback
    .filter((f) => {
      if (filterCategory && f.category !== filterCategory) return false;
      if (filterRating && f.rating !== parseInt(filterRating)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'nps') return b.npsScore - a.npsScore;
      return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
    });

  const ratingBarData = analytics
    ? Object.entries(analytics.ratingDistribution)
        .map(([star, count]) => ({
          star: `${star}★`,
          count,
          percent: analytics.totalResponses > 0 ? Math.round((count / analytics.totalResponses) * 100) : 0,
        }))
        .reverse()
    : [];

  const categoryPieData = analytics
    ? Object.entries(analytics.categoryBreakdown).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-2">Feedback Analytics</h1>
      <p className="text-gray-400 mb-8">Insights from your beta users</p>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <SkeletonMetricCard key={i} />)}
          </div>
          <SkeletonChart />
        </div>
      ) : !analytics || analytics.totalResponses === 0 ? (
        <div className="card text-center py-16">
          <div className="text-4xl mb-3">💬</div>
          <h2 className="text-xl font-bold text-white mb-2">No feedback yet</h2>
          <p className="text-gray-400 mb-4">Generate invite links in the Beta tab to start collecting feedback.</p>
          {/* Polish 2: Direct CTA to Beta Users */}
          <button
            onClick={() => navigate(`/startups/${startupId}/beta`)}
            className="btn-primary"
          >
            → Go to Beta Users
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Key Metrics ──────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* NPS */}
            <div className="card col-span-2 lg:col-span-1">
              <p className="text-gray-500 text-xs mb-1">NPS Score</p>
              <p className="text-4xl font-black mb-2" style={{ color: NPS_COLOR(analytics.npsScore) }}>
                {analytics.npsScore > 0 ? '+' : ''}{analytics.npsScore}
              </p>
              <div className="flex gap-3 text-xs">
                <span className="text-green-400">✓ {analytics.npsBreakdown.promoters} Promoters</span>
                <span className="text-yellow-400">◌ {analytics.npsBreakdown.passives} Passives</span>
                <span className="text-red-400">✗ {analytics.npsBreakdown.detractors} Detractors</span>
              </div>
            </div>
            <div className="card">
              <p className="text-gray-500 text-xs mb-1">Avg Rating</p>
              <p className="text-3xl font-bold text-white mb-1">{analytics.averageRating.toFixed(1)}</p>
              <StarRating value={Math.round(analytics.averageRating)} readonly size="sm" />
            </div>
            <div className="card">
              <p className="text-gray-500 text-xs mb-1">Responses</p>
              <p className="text-3xl font-bold text-white">{analytics.totalResponses}</p>
            </div>
            <div className="card">
              <p className="text-gray-500 text-xs mb-1">Would Recommend</p>
              <p className="text-3xl font-bold text-green-400">{analytics.recommendationRate.yes}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.recommendationRate.no}% No · {analytics.recommendationRate.maybe}% Maybe
              </p>
            </div>
          </div>

          {/* ── Charts Row ──────────────────────────────────── */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Rating Distribution */}
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Rating Distribution</h2>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ratingBarData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis dataKey="star" type="category" tick={{ fill: '#6b7280', fontSize: 11 }} width={25} />
                  <Tooltip
                    content={({ active, payload }) => active && payload?.length ? (
                      <div className="bg-[#161627] border border-[#2a2a3e] rounded-lg p-2 text-sm">
                        <p className="text-white">{payload[0].payload.star}: {payload[0].value} ({payload[0].payload.percent}%)</p>
                      </div>
                    ) : null}
                  />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Category Breakdown */}
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Category Breakdown</h2>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={categoryPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`}
                    labelLine={false}
                  >
                    {categoryPieData.map((entry) => (
                      <Cell key={entry.name} fill={CATEGORY_COLORS[entry.name] ?? '#6366f1'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Top Feature Requests ──────────────────────── */}
          {analytics.topFeatureRequests.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Feature Request Ranking</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#2a2a3e]">
                    <th className="text-left py-2 text-gray-500 font-medium">Feature</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Requested by</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Priority Score</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.topFeatureRequests.map((fr, i) => (
                    <tr key={fr.name} className="border-b border-[#2a2a3e]">
                      <td className="py-2">
                        <span className="text-gray-500 mr-2">#{i + 1}</span>
                        <span className="text-white">{fr.name}</span>
                      </td>
                      <td className="py-2 text-right text-gray-400">{fr.count} user{fr.count !== 1 ? 's' : ''}</td>
                      <td className="py-2 text-right">
                        <span className="text-brand-500 font-medium">{fr.priorityScore.toFixed(1)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Individual Feedback ───────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">All Feedback</h2>
              <div className="flex gap-2 flex-wrap">
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="input-field text-sm w-auto">
                  <option value="">All categories</option>
                  {Object.keys(CATEGORY_COLORS).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)} className="input-field text-sm w-auto">
                  <option value="">All ratings</option>
                  {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r}★</option>)}
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className="input-field text-sm w-auto">
                  <option value="newest">Newest first</option>
                  <option value="rating">Highest rating</option>
                  <option value="nps">Highest NPS</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              {filteredFeedback.map((fb) => (
                <div key={fb.id} className="card">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white text-sm">
                        {typeof fb.betaUser === 'object' ? fb.betaUser.name : 'Anonymous'}
                      </p>
                      <p className="text-gray-500 text-xs">{new Date(fb.submittedAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="badge" style={{ backgroundColor: CATEGORY_COLORS[fb.category] + '20', color: CATEGORY_COLORS[fb.category] }}>
                        {fb.category}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${fb.npsScore >= 9 ? 'bg-green-900/40 text-green-400' : fb.npsScore >= 7 ? 'bg-yellow-900/40 text-yellow-400' : 'bg-red-900/40 text-red-400'}`}>
                        NPS {fb.npsScore}
                      </span>
                    </div>
                  </div>
                  <StarRating value={fb.rating} readonly size="sm" />
                  <p className="text-gray-300 text-sm mt-3">{fb.comment}</p>

                  {fb.featureRequests.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {fb.featureRequests.map((fr, i) => (
                        <div key={i} className="bg-[#0f0f1a] rounded-lg px-3 py-2 text-xs">
                          <span className="text-brand-500 font-medium">→ {fr.name}</span>
                          <span className="text-gray-500 ml-2">{fr.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-[#2a2a3e] flex items-center gap-4 text-xs">
                    <span className={`${fb.wouldRecommend === 'yes' ? 'text-green-400' : fb.wouldRecommend === 'no' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {fb.wouldRecommend === 'yes' ? '👍 Would recommend' : fb.wouldRecommend === 'no' ? '👎 Would not recommend' : '🤔 Maybe recommend'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
